import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import {
  OnboardingAction,
  OnboardingContent,
  OnboardingContentUploadResponse,
  OnboardingDownloadResponse,
  OnboardingFileCategory,
  OnboardingUploadResponse,
  OnboardingView,
} from '@chiroup/core/types/Onboarding.type';

const onboardingService = () => {
  const get = (clinicId: number): Promise<OnboardingView> =>
    ChiroUpAPI.get('api', `/clinics/${clinicId}/onboarding`, {});

  const act = (clinicId: number, action: OnboardingAction): Promise<OnboardingView> =>
    ChiroUpAPI.post('api', `/clinics/${clinicId}/onboarding`, { body: action });

  const upload = async (
    clinicId: number,
    file: File,
    category: OnboardingFileCategory,
    fileId?: string
  ): Promise<OnboardingView> => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    const contentType =
      extension === 'csv'
        ? 'text/csv'
        : extension === 'xls'
          ? 'application/vnd.ms-excel'
          : extension === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/pdf';
    const prepared: OnboardingUploadResponse = await ChiroUpAPI.post(
      'api',
      `/clinics/${clinicId}/onboarding`,
      {
        body: {
          type: 'prepareFile',
          category,
          fileId,
          name: file.name,
          contentType,
          size: file.size,
        },
      }
    );
    const form = new FormData();
    Object.entries(prepared.fields).forEach(([key, value]) => form.append(key, value));
    form.append('file', file);
    const response = await fetch(prepared.url, { method: 'POST', body: form });
    if (!response.ok) throw new Error('The file could not be uploaded. Please try again.');
    return act(clinicId, {
      type: 'submitFile',
      category,
      fileId,
      versionId: prepared.versionId,
      key: prepared.key,
      name: file.name,
      contentType,
      size: file.size,
    });
  };

  const download = (
    clinicId: number,
    fileId: string,
    versionId?: string
  ): Promise<OnboardingDownloadResponse> =>
    ChiroUpAPI.post('api', `/clinics/${clinicId}/onboarding`, {
      body: { type: 'downloadFile', fileId, versionId },
    });

  const getContent = (): Promise<OnboardingContent> =>
    ChiroUpAPI.get('api', '/onboarding/content', {});
  const saveContent = (content: OnboardingContent): Promise<OnboardingContent> =>
    ChiroUpAPI.put('api', '/onboarding/content', { body: content });

  const uploadContentAsset = async (file: File): Promise<string> => {
    const prepared: OnboardingContentUploadResponse = await ChiroUpAPI.post(
      'api',
      '/onboarding/content/upload',
      {
        body: { name: file.name, contentType: file.type, size: file.size },
      }
    );
    const form = new FormData();
    Object.entries(prepared.fields).forEach(([key, value]) => form.append(key, value));
    form.append('file', file);
    const response = await fetch(prepared.url, { method: 'POST', body: form });
    if (!response.ok) throw new Error('The resource could not be uploaded. Please try again.');
    return prepared.assetUrl;
  };

  return { get, act, upload, download, getContent, saveContent, uploadContentAsset };
};

export default onboardingService();
